import React, { useEffect, useState } from 'react';
import { Mail, Phone, Search, Check, X, Eye } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import { Panigation } from '@/components/ui/Panigation';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchAffiliateMembers } from './function';
// import { useTranslation } from 'react-i18next';
import Spinner from '@/components/Spinner';

type Member = {
  id: string;
  name: string;
  dob: string;
  mail: string;
  phone: string;
  planPackage: {
    package_name: string;
    quantity: number;
    total_amount: string;
  }[];
  totalSpend: string;
  kycStatus: boolean;
  status: 'ACTIVE' | 'UNACTIVE';
  deposit: 'PAID' | 'UNPAID' | 'OVERDUE';
};

const MemberManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentFilter, setPaymentFilter] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  // const { t } = useTranslation();

  const [memberData, setMemberData] = useState<Member[]>([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetchAffiliateMembers(currentPage, 10, searchTerm);
        setMemberData(res.data.data.map(member => ({
          id: member.id.toString(),
          name: member.user_info ? member.user_info.fullname : 'N/A',
          dob: member.user_info ? member.user_info.birthday || 'N/A' : 'N/A',
          mail: member.user_info ? member.user_info.email : 'N/A',
          phone: member.user_info ? member.user_info.phone || 'Chưa có' : 'N/A',
          planPackage: member.packages_detail,
          totalSpend: member.total_invested || '0',
          kycStatus: member.kyc_status || false,
          status: 'ACTIVE',
          deposit: member.deposit_status ? 'PAID' : 'UNPAID',
        })));
        setTotalData(res.data.total);
        console.log('Fetched members:', res);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage, searchTerm]);

  const openMemberDetails = (memberId: string) => {
    navigate(`/members/${memberId}`);
  }

  const getPageTitle = () => {
    if (location.pathname.includes('referrals')) return 'Referrals';
    if (location.pathname.includes('billing')) return 'Billing';
    return 'Danh sách thành viên';
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', path: '/home' },
    { label: getPageTitle() },
  ];

  return (

    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>Danh sách thành viên - BeQs Reward</title>
      </Helmet>
      <PageHeader title="Danh sách thành viên" breadcrumbs={breadcrumbs} />

      <div className="mb-6">
        <p className="text-gray-800 dark:text-gray-400 mt-1">
          <span>Tổng số thành viên: {totalData}</span>
        </p>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Tìm kiếm theo tên hoặc ID thành viên..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="pl-4 pr-8 py-2.5 w-48 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Tất cả</option>
          <option value="PAID">Đã thanh toán</option>
          <option value="UNPAID">Chưa thanh toán</option>
          <option value="OVERDUE">Quá hạn</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Thành viên</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Liên hệ</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gói đầu tư</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tổng đầu tư</th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">KYC</th> */}
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trạng thái</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Thanh toán</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hành động</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {
                loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 whitespace-nowrap  w-full text-center text-sm text-gray-700 dark:text-gray-300">
                      <span className="flex items-center justify-center gap-2"><Spinner size={32} /> Loading...</span>
                    </td>
                  </tr>
                ) : memberData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                      Không tìm thấy thành viên nào.
                    </td>
                  </tr>
                ) :
                  (
                    memberData.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-start text-sm text-gray-700 dark:text-gray-300">
                          <span>{member.name}</span>
                          <br />
                          <span className="text-xs text-gray-400 dark:text-gray-500">{member.dob}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center gap-3">
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              <div className='flex items-center gap-1'>
                                <Mail className='w-3 h-3 !text-gray-500 dark:!text-gray-400' />
                                <span>{member.mail}</span>
                              </div>
                              <div className="text-xs !text-gray-400 dark:!text-gray-500">
                                <div className='flex items-center gap-1'>
                                  <Phone className='w-3 h-3 !text-gray-500 dark:!text-gray-400' />
                                  <span>{member.phone}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                          {member.planPackage.map((pkg, index) => (
                            <div key={index} className="mb-1 flex flex-col items-start gap-2">
                              <span className="font-medium">Gói: {pkg.package_name}</span>  
                              <span>Số lượng: {pkg.quantity}</span>
                            </div>
                          ))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                          {new Intl.NumberFormat('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          }).format(Number(member.totalSpend))}
                        </td>
                        {/* <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center">
                            {member.kycStatus ? (
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30">
                                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                            ) : (
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30">
                                <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </div>
                            )}
                          </div>
                        </td> */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${member.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                            {member.status === 'ACTIVE' ? 'Hoạt động' : 'Không hoạt động'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            {member.deposit === 'PAID' ? (
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30">
                                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                            ) : (
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30">
                                <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <button
                              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                              onClick={() => openMemberDetails(member.id)}
                            >
                              <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
              }
              { }
            </tbody>
          </table>
        </div>
        <Panigation currentPage={currentPage} totalPages={Math.ceil(totalData / 10)} onPageChange={setCurrentPage} />
      </div>
    </div>
  );
};

export default MemberManagement;
